import React from 'react';
import { ParsedQuery } from 'query-string';
import translationNL from './locales/dutch.json';
import translationEN from './locales/english.json';
import { QTheme, LangType, QSpinner } from '@kubiq-base/core';
import { BrowserRouter } from 'react-router-dom';
import { useApi, ApiProvider } from './state/ApiContext';
import { useAppContext, AppProvider } from './state/AppContext';
import { CacheProvider, useCache } from './state/CachingContext';
import { ITransparentMode } from './interfaces/';
import SiteHelper from './helpers/SiteHelper';
import ApiHelper from './helpers/ApiHelper';
import Main from './components/Main';

const translations = {
  [LangType.NL]: { ...translationNL },
  [LangType.EN]: { ...translationEN },
};

const AppComponent = (): JSX.Element => {
  const { postRequest, getRequest } = useApi();
  const { state, setEmail, setTokens, clearTokens } = useAppContext();
  const { getCache, setCache, clearCache } = useCache();
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const startFresh = (): void => {
    setEmail('');
    clearTokens();
    setIsRedirecting(false);
  };

  const parseUrl = (): ParsedQuery<string> => {
    const parsed = SiteHelper.parseUrl();

    // Set all query string data in a seperate context. And strip the querystring
    if (Object.keys(parsed).length > 0) {
      if (parsed.returnurl && parsed.returnurl === 'app') {
        delete parsed.returnurl;

        parsed.source = 'app';
      }

      if (parsed.organisationsapplicationsguid) clearCache('my');
      else if (parsed.my) {
        clearCache('organisationsapplicationsguid');
        clearCache('returnurl');
      }

      Object.keys(parsed).map((key) => setCache(key, parsed[key]));
    }

    SiteHelper.stripQueryString(); // Clear query string for a clean URL
    return parsed;
  };

  const setup = (): void => {
    // clearCache('email');
    clearCache('verifiedPhone');

    let queryString = parseUrl();
    if (Object.keys(queryString).length === 0) queryString = getCache();

    const isOnExcludedPage = [
      'resetpassword/verify',
      'resetauthenticator/verify',
      'recovery',
      'logout',
      'verifyphone',
    ].some((path) => location.pathname.toLowerCase().includes(path));

    const hasErrors = queryString.error;

    if (getCache('email') || state.email) {
      getRequest<{ data: { emailExists: boolean; verifiedPhone: boolean } }>('User/Emailcheck', [
        { name: 'email', value: getCache('email') || state.email },
      ]).then((result) => {
        if (result.data) setCache('verifiedPhone', result.data.verifiedPhone);
      });
    }

    if (queryString.requestid && (queryString.organisationsapplicationsguid || queryString.my) && !isOnExcludedPage) {
      setIsRedirecting(true);

      if (queryString.organisationsapplicationsguid) {
        getRequest<{ data: ITransparentMode }>('User/TransparentMode', [
          { name: 'requestId', value: queryString.requestid as string },
          { name: 'organisationApplicationGuid', value: queryString.organisationsapplicationsguid as string },
        ]).then((result) => {
          if (result.data.returnUrl) setCache('returnurl', result.data.returnUrl);

          if (!hasErrors) {
            if (!result.data.transparentMode) {
              if (state.accessToken && state.refreshToken && state.email) {
                SiteHelper.getUserOADetails(state.email, queryString, setCache, getRequest)
                  .then((result) => {
                    SiteHelper.refreshToken(
                      state,
                      { ...queryString, returnurl: result.returnUrl },
                      setTokens,
                      clearCache,
                      postRequest,
                      startFresh,
                    );
                  })
                  .catch(() => startFresh());
              } else startFresh();
            } else {
              ApiHelper.redirect(result.data.redirectUrl as string);
            }
          }
        });
      } else if (queryString.my) {
        if (state.accessToken && state.refreshToken && state.email) {
          SiteHelper.getUserOADetails(state.email, queryString, setCache, getRequest)
            .then(() => {
              SiteHelper.refreshToken(
                state,
                { ...queryString, returnurl: 'my' },
                setTokens,
                clearCache,
                postRequest,
                startFresh,
              );
            })
            .catch(() => startFresh());
        } else startFresh();
      }
    }

    setIsLoaded(true);
  };

  React.useEffect(() => {
    setup();
  }, []);

  return (
    <>
      {isLoaded && !isRedirecting ? (
        <Main />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <QSpinner />
        </div>
      )}
    </>
  );
};

const App = (): JSX.Element => {
  return (
    <div className='App'>
      <BrowserRouter>
        <QTheme translations={translations}>
          <AppProvider>
            {({ accessToken }) => (
              <ApiProvider accessToken={accessToken}>
                <CacheProvider>
                  <AppComponent />
                </CacheProvider>
              </ApiProvider>
            )}
          </AppProvider>
        </QTheme>
      </BrowserRouter>
    </div>
  );
};

export default App;
